import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  BluetoothAudio,
  Business,
  GraphicEq,
  Headset,
  Memory,
  Mic,
  PhoneAndroid,
  Refresh,
  ShowChart,
} from "@material-ui/icons";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { getCalibrationStatistics } from "../utils/NetworkHandler";

ChartJS.register(ArcElement, Tooltip, Legend);

const FREQUENCIES = [500, 1000, 2000, 4000];

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 1680,
    margin: "0 auto",
    color: theme.palette.text.primary,
  },
  hero: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 16,
    color: "white",
    background:
      "linear-gradient(120deg, #244aa5 0%, #315fc7 55%, #237ea4 100%)",
    boxShadow: "0 12px 32px rgba(36, 74, 165, 0.2)",
  },
  heroOverline: {
    opacity: 0.76,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  heroText: {
    maxWidth: 760,
    marginTop: theme.spacing(0.75),
    opacity: 0.9,
  },
  refreshButton: {
    color: "white",
    borderColor: "rgba(255,255,255,0.6)",
  },
  sectionTitle: {
    margin: theme.spacing(3, 0, 1.5),
    fontWeight: 700,
  },
  metricCard: {
    height: "100%",
    borderRadius: 14,
    border: "1px solid #e7ebf3",
    boxShadow: "0 4px 16px rgba(30, 52, 91, 0.07)",
  },
  metricContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2.25),
    "&:last-child": { paddingBottom: theme.spacing(2.25) },
  },
  metricIcon: {
    width: 48,
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 13,
    color: "#315fc7",
    backgroundColor: "#edf2ff",
    flexShrink: 0,
  },
  metricValue: {
    fontSize: "1.75rem",
    lineHeight: 1.1,
    fontWeight: 700,
  },
  panel: {
    flex: 1,
    borderRadius: 14,
    border: "1px solid #e7ebf3",
    boxShadow: "0 4px 16px rgba(30, 52, 91, 0.07)",
  },
  overviewColumn: {
    display: "flex",
    flexDirection: "column",
  },
  chartWrap: {
    height: 260,
    display: "flex",
    justifyContent: "center",
  },
  platformSummary: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  activityCard: {
    height: "100%",
    padding: theme.spacing(2.25),
    borderRadius: 14,
    border: "1px solid #e7ebf3",
    boxShadow: "0 4px 16px rgba(30, 52, 91, 0.07)",
  },
  activityHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minHeight: 48,
  },
  date: {
    fontSize: "1.35rem",
    fontWeight: 700,
    margin: theme.spacing(1.25, 0),
  },
  activityCounts: {
    display: "flex",
    justifyContent: "space-between",
    paddingTop: theme.spacing(1.25),
    borderTop: "1px solid #edf0f5",
  },
  rankingCard: {
    borderRadius: 14,
    border: "1px solid #e7ebf3",
    boxShadow: "0 4px 16px rgba(30, 52, 91, 0.07)",
    overflow: "hidden",
  },
  rankingHeader: {
    padding: theme.spacing(2, 2.5),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "linear-gradient(90deg, #f8faff 0%, #fff 100%)",
    borderBottom: "1px solid #e7ebf3",
  },
  tableContainer: {
    maxHeight: 360,
  },
  stickyHead: {
    backgroundColor: "#f7f9fc",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  rank: {
    color: theme.palette.text.secondary,
    width: 42,
  },
  deviationStrong: {
    fontWeight: 700,
    color: "#244aa5",
  },
  splitEntityName: {
    display: "inline-flex",
    flexDirection: "column",
    lineHeight: 1.35,
  },
  entityNameLine: {
    whiteSpace: "nowrap",
  },
  loading: {
    minHeight: 440,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  error: {
    padding: theme.spacing(3),
    borderRadius: 12,
    border: "1px solid #f0c8c8",
    backgroundColor: "#fff7f7",
  },
}));

function formatDate(value) {
  if (!value) return "Brak danych";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Brak danych";
  return [
    String(date.getDate()).padStart(2, "0"),
    String(date.getMonth() + 1).padStart(2, "0"),
    date.getFullYear(),
  ].join("-");
}

function formatDeviation(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${Number(value).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} dB`;
}

export function filterManufacturerDeviationRows(rows = []) {
  return (rows || []).filter(
    (row) =>
      Number(row?.includedChildren) > 1 &&
      String(row?.name || "").trim().toLocaleLowerCase("pl-PL") !== "apple"
  );
}

export function filterBrandDeviationRows(rows = []) {
  return (rows || []).filter((row) => Number(row?.includedChildren) > 1);
}

export function splitBrandDeviationName(name) {
  const value = String(name || "");
  const separatorIndex = value.indexOf(" / ");
  if (separatorIndex < 0) return null;

  return {
    manufacturer: value.slice(0, separatorIndex),
    brand: value.slice(separatorIndex + 3),
  };
}

function DeviationEntityName({ name, splitEntityName, classes }) {
  const splitName = splitEntityName ? splitBrandDeviationName(name) : null;
  if (!splitName) return name;

  return (
    <span className={classes.splitEntityName}>
      <span className={classes.entityNameLine}>{splitName.manufacturer} /</span>
      <span className={classes.entityNameLine}>{splitName.brand}</span>
    </span>
  );
}

export function MetricCard({ icon, value, label, detail }) {
  const classes = useStyles();
  return (
    <Card className={classes.metricCard} elevation={0}>
      <CardContent className={classes.metricContent}>
        <Box className={classes.metricIcon}>{icon}</Box>
        <Box>
          <Typography className={classes.metricValue}>{value || 0}</Typography>
          <Typography variant="body2">{label}</Typography>
          {detail && (
            <Typography variant="caption" color="textSecondary">
              {detail}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export function ActivityCard({ icon, title, activity, modelLabel, totalLabel }) {
  const classes = useStyles();
  return (
    <Paper className={classes.activityCard} elevation={0}>
      <Box className={classes.activityHeader}>
        <Box className={classes.metricIcon}>{icon}</Box>
        <Typography variant="subtitle2">{title}</Typography>
      </Box>
      <Typography className={classes.date}>{formatDate(activity?.latestDate)}</Typography>
      <Box className={classes.activityCounts}>
        <Box>
          <Typography variant="h6">{activity?.distinctModels || 0}</Typography>
          <Typography variant="caption" color="textSecondary">
            {modelLabel}
          </Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="h6">{activity?.total || 0}</Typography>
          <Typography variant="caption" color="textSecondary">
            {totalLabel}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export function DeviationTable({
  title,
  subtitle,
  childrenLabel,
  rows = [],
  splitEntityName = false,
}) {
  const classes = useStyles();
  const showChildrenCount = Boolean(childrenLabel);
  return (
    <Paper className={classes.rankingCard} elevation={0}>
      <Box className={classes.rankingHeader}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="caption" color="textSecondary">
            {subtitle}
          </Typography>
        </Box>
        <Chip size="small" label={`${rows.length} pozycji`} variant="outlined" />
      </Box>
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader size="small" aria-label={`Ranking odchyleń: ${title}`}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.stickyHead}>#</TableCell>
              <TableCell className={classes.stickyHead}>{title}</TableCell>
              {showChildrenCount && (
                <TableCell className={classes.stickyHead} align="right">
                  {childrenLabel}
                </TableCell>
              )}
              {FREQUENCIES.map((frequency) => (
                <TableCell
                  key={frequency}
                  className={classes.stickyHead}
                  align="right"
                >
                  {frequency} Hz
                </TableCell>
              ))}
              <TableCell className={classes.stickyHead} align="right">
                Maksimum
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showChildrenCount ? 8 : 7} align="center">
                  Brak danych do obliczenia odchylenia
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={`${row.name}-${index}`} hover>
                  <TableCell className={classes.rank}>{index + 1}</TableCell>
                  <TableCell>
                    <DeviationEntityName
                      name={row.name}
                      splitEntityName={splitEntityName}
                      classes={classes}
                    />
                  </TableCell>
                  {showChildrenCount && (
                    <TableCell align="right">{row.includedChildren}</TableCell>
                  )}
                  {FREQUENCIES.map((frequency) => (
                    <TableCell key={frequency} align="right">
                      {formatDeviation(
                        row.standardDeviationByFrequency?.[frequency]
                      )}
                    </TableCell>
                  ))}
                  <TableCell
                    className={classes.deviationStrong}
                    align="right"
                  >
                    {formatDeviation(row.maximumStandardDeviation)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function CalibrationStatisticsPage() {
  const classes = useStyles();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const mounted = useRef(false);

  const loadStatistics = useCallback(async (force = false) => {
    setLoading(true);
    setError(false);
    try {
      const response = await getCalibrationStatistics({ force });
      if (mounted.current) setStatistics(response?.data || null);
    } catch (requestError) {
      if (mounted.current) setError(true);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadStatistics();
    return () => {
      mounted.current = false;
    };
  }, [loadStatistics]);

  const chartData = useMemo(() => {
    const android = statistics?.platformDistribution?.android || 0;
    const ios = statistics?.platformDistribution?.ios || 0;
    return {
      labels: ["Android", "iPhone"],
      datasets: [
        {
          data: [android, ios],
          backgroundColor: ["#315fc7", "#52b6a8"],
          borderColor: ["#ffffff", "#ffffff"],
          borderWidth: 3,
          hoverOffset: 7,
        },
      ],
    };
  }, [statistics]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "64%",
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => `${context.label}: ${context.parsed}`,
          },
        },
      },
    }),
    []
  );

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={46} />
        <Typography color="textSecondary">Pobieranie statystyk kalibracji…</Typography>
      </Box>
    );
  }

  if (error || !statistics) {
    return (
      <Box className={classes.error}>
        <Typography variant="h6">Nie udało się pobrać statystyk</Typography>
        <Typography color="textSecondary" paragraph>
          Sprawdź połączenie z backendem i spróbuj ponownie.
        </Typography>
        <Button
          color="primary"
          variant="contained"
          onClick={() => loadStatistics(true)}
        >
          Spróbuj ponownie
        </Button>
      </Box>
    );
  }

  const coverage = statistics.coverage || {};
  const platforms = statistics.platformDistribution || {};
  const manufacturerDeviationRows = filterManufacturerDeviationRows(
    statistics.manufacturers
  );
  const brandDeviationRows = filterBrandDeviationRows(statistics.brands);
  const microphoneCoverage = coverage.models
    ? Math.round(
        (100 * coverage.modelsWithMicrophoneAndSpeakerCalibration) /
          coverage.models
      )
    : 0;

  return (
    <Box className={classes.root}>
      <Paper className={classes.hero} elevation={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography className={classes.heroOverline} variant="overline">
              CENTRUM JAKOŚCI KALIBRACJI
            </Typography>
            <Typography variant="h4">Statystyki kalibracji</Typography>
            <Typography className={classes.heroText} variant="body2">
              Skalibrowane urządzenia, ostatnia aktywność i analiza odchylenia
              standardowego dla szacowanych maksymalnych głośności uśrednionych
              kalibracji po różnych poziomach uśrednienia.
            </Typography>
          </Box>
          <Button
            className={classes.refreshButton}
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => loadStatistics(true)}
          >
            Odśwież
          </Button>
        </Box>
      </Paper>

      <Typography className={classes.sectionTitle} variant="h6">
        Pokrycie kalibracjami
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md>
          <MetricCard icon={<Memory />} value={coverage.models} label="Modele" />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <MetricCard icon={<PhoneAndroid />} value={coverage.brands} label="Marki" />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <MetricCard
            icon={<Business />}
            value={coverage.manufacturers}
            label="Producenci"
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <MetricCard
            icon={<Mic />}
            value={coverage.modelsWithMicrophoneAndSpeakerCalibration}
            label="Mikrofon + głośnik"
            detail={`${microphoneCoverage}% skalibrowanych modeli`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <MetricCard
            icon={<BluetoothAudio />}
            value={coverage.modelsWithBluetoothSpeakerCalibration}
            label="Modele z kalibracją głośnika BT"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid className={classes.overviewColumn} item xs={12} md={4}>
          <Typography className={classes.sectionTitle} variant="h6">
            Platformy urządzeń
          </Typography>
          <Card className={classes.panel} elevation={0}>
            <CardContent>
              <Box className={classes.chartWrap}>
                <Doughnut data={chartData} options={chartOptions} />
              </Box>
              <Box className={classes.platformSummary}>
                <Chip label={`Android ${platforms.android || 0}`} color="primary" />
                <Chip label={`iPhone ${platforms.ios || 0}`} variant="outlined" />
              </Box>
              <Box mt={2} textAlign="center">
                <Typography variant="caption" color="textSecondary">
                  Liczba unikalnych uśrednień po modelu; Apple jest klasyfikowane
                  jako iPhone.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid className={classes.overviewColumn} item xs={12} md={8}>
          <Box className={classes.sectionTitle}>
            <Typography variant="h6">Ostatnia aktywność</Typography>
            <Typography variant="caption" color="textSecondary">
              Liczniki pokazują pracę wykonaną wyłącznie w widocznym dniu.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ActivityCard
                icon={<GraphicEq />}
                title="Kalibracja głośnika telefonu"
                activity={statistics.phoneSpeakerCalibrations}
                modelLabel="modeli tego dnia"
                totalLabel="kalibracji tego dnia"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ActivityCard
                icon={<Headset />}
                title="Odsłuch głośnika telefonu"
                activity={statistics.phoneSpeakerListenings}
                modelLabel="modeli tego dnia"
                totalLabel="odsłuchów tego dnia"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ActivityCard
                icon={<Mic />}
                title="Kalibracja mikrofonu"
                activity={statistics.microphoneCalibrations}
                modelLabel="modeli tego dnia"
                totalLabel="kalibracji tego dnia"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ActivityCard
                icon={<BluetoothAudio />}
                title="Kalibracja głośnika BT"
                activity={statistics.bluetoothSpeakerCalibrations}
                modelLabel="modeli tego dnia"
                totalLabel="kalibracji tego dnia"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Box display="flex" alignItems="center" mt={4} mb={1.5}>
        <ShowChart color="primary" />
        <Box ml={1}>
          <Typography variant="h6">Zmienność maksymalnej intensywności</Typography>
          <Typography variant="body2" color="textSecondary">
            Odchylenie standardowe szacowanych maksymalnych głośności kalibracji
            podpiętych pod dany poziom uśrednienia
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DeviationTable
            title="System operacyjny"
            subtitle="Odchylenie na poziomie systemu operacyjnego"
            rows={statistics.operatingSystems}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <DeviationTable
            title="Producent"
            subtitle="Odchylenie standardowe dla producentów mających więcej niż jedną markę, których system operacyjny to Android"
            childrenLabel="Marki"
            rows={manufacturerDeviationRows}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <DeviationTable
            title="Marka"
            subtitle="Odchylenie standardowe dla marek mających więcej niż jeden model, których system operacyjny to Android"
            childrenLabel="Modele"
            rows={brandDeviationRows}
            splitEntityName
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export { formatDate, formatDeviation };
